/**
 * EssayQuestion.tsx
 * V-SAT 2025 Essay question with word count and auto-save
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Save, AlertCircle, CheckCircle } from 'lucide-react';
import { Card } from '@vsat/shared/components';
import { Button } from '@vsat/shared/components';
import { Badge } from '@vsat/shared/components';
import { cn } from '@vsat/shared/lib/utils';

interface Props {
  value: string;
  onChange: (value: string) => void;
  minWords?: number;
  maxWords?: number;
  readOnly?: boolean;
  autoSave?: boolean;
  onAutoSave?: (content: string) => void;
}

export default function EssayQuestion({
  value,
  onChange,
  minWords = 0,
  maxWords = 1000,
  readOnly = false,
  autoSave = true,
  onAutoSave,
}: Props) {
  const { t } = useTranslation();
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Word count calculation
  const wordCount = value.trim() ? value.trim().split(/\s+/).length : 0;
  const isMinWordsMet = wordCount >= minWords;
  const isMaxWordsExceeded = wordCount > maxWords;

  // Auto-save functionality
  useEffect(() => {
    if (!autoSave || !onAutoSave || readOnly) return;

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      if (value.trim()) {
        setIsSaving(true);
        onAutoSave(value);
        setTimeout(() => {
          setIsSaving(false);
          setLastSaved(new Date());
        }, 500);
      }
    }, 30000); // Auto-save every 30 seconds

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [value, autoSave, onAutoSave, readOnly]);

  const handleChange = (newValue: string) => {
    if (readOnly) return;
    // Allow typing beyond maxWords but warning will be shown
    onChange(newValue);
  };

  const handleManualSave = () => {
    if (onAutoSave && value.trim()) {
      setIsSaving(true);
      onAutoSave(value);
      setTimeout(() => {
        setIsSaving(false);
        setLastSaved(new Date());
      }, 500);
    }
  };

  const getWordCountColor = () => {
    if (isMaxWordsExceeded) return 'text-rose-600 dark:text-rose-400';
    if (minWords > 0 && !isMinWordsMet) return 'text-amber-600 dark:text-amber-400';
    return 'text-slate-500 dark:text-slate-400';
  };

  const getWordCountBadgeVariant = () => {
    if (isMaxWordsExceeded) return 'destructive';
    if (minWords > 0 && !isMinWordsMet) return 'secondary';
    return 'outline' as const;
  };

  // Adaptive config based on expected answer length
  // Q1-Q2: ≤150 words → tiny compact textarea, no heavy guidelines
  // Q3-Q6: 151-300 words → medium textarea with guidelines
  // Q7: 600 words → full height textarea with all guidelines
  const isShortAnswer = maxWords <= 150;
  const isMediumAnswer = maxWords > 150 && maxWords <= 300;
  const textareaMinHeight = isShortAnswer
    ? 'min-h-[100px]'
    : isMediumAnswer
      ? 'min-h-[200px]'
      : 'min-h-[380px]';
  const textareaMaxHeight = isShortAnswer
    ? 'max-h-[200px]'
    : isMediumAnswer
      ? 'max-h-[320px]'
      : 'max-h-[560px]';

  return (
    <div className='space-y-4'>
      {/* Header with word count and save status - only show when minWords > 0 */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          {minWords > 0 && (
            <Badge variant={getWordCountBadgeVariant()} className='text-xs'>
              {t('landing.questions.essay.wordCount', { current: wordCount, max: maxWords })}
            </Badge>
          )}
          {minWords === 0 && wordCount > 0 && (
            <Badge variant={isMaxWordsExceeded ? 'destructive' : 'secondary'} className='text-xs'>
              {t('landing.questions.essay.wordCount', { current: wordCount, max: maxWords })}
            </Badge>
          )}
        </div>

        <div className='flex items-center gap-2'>
          {autoSave && lastSaved && (
            <div className='flex items-center gap-1 text-xs text-muted-foreground'>
              <CheckCircle className='w-3 h-3 text-primary ' />
              <span>
                {t('landing.questions.essay.saved', { time: lastSaved.toLocaleTimeString() })}
              </span>
            </div>
          )}

          {isSaving && (
            <div className='flex items-center gap-1 text-xs text-primary'>
              <div className='w-3 h-3 border-2 border-primary  border-t-transparent rounded-full animate-spin' />
              <span>{t('landing.questions.essay.saving')}</span>
            </div>
          )}

          {!readOnly && (
            <Button
              variant='outline'
              size='sm'
              onClick={handleManualSave}
              disabled={isSaving || !value.trim()}
              className='h-7 px-2 text-xs border-border hover:bg-primary/10 hover:border-primary/30 transition-all duration-300'
            >
              <Save className='w-3 h-3 mr-1' />
              {t('landing.questions.essay.save')}
            </Button>
          )}
        </div>
      </div>

      {/* Over-limit warning */}
      {isMaxWordsExceeded && (
        <div className='flex items-center gap-2 p-3 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-lg'>
          <AlertCircle className='w-4 h-4 text-rose-600 dark:text-rose-400 flex-shrink-0' />
          <p className='text-xs text-rose-700 dark:text-rose-300'>
            {t('landing.questions.essay.overLimit', { count: wordCount - maxWords, max: maxWords })}
          </p>
        </div>
      )}

      {/* Under-limit warning - only show if minWords > 0 */}
      {minWords > 0 && !isMinWordsMet && wordCount > 0 && (
        <div className='flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg'>
          <AlertCircle className='w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0' />
          <p className='text-xs text-amber-700 dark:text-amber-300'>
            <Trans
              i18nKey='landing.questions.essay.needMoreWords'
              values={{ count: minWords - wordCount }}
              components={{ strong: <strong className='font-bold' /> }}
            />
          </p>
        </div>
      )}

      {/* Text editor */}
      <Card
        className={cn(
          'p-0 overflow-hidden border transition-all duration-300',
          'hover:shadow-md hover:ring-2 hover:ring-primary/20',
          isMaxWordsExceeded
            ? 'border-rose-300 dark:border-rose-700 ring-1 ring-rose-300'
            : 'border-border'
        )}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={e => handleChange(e.target.value)}
          disabled={readOnly}
          placeholder={t('landing.questions.essay.placeholder', { minWords })}
          className={cn(
            'w-full p-4 border-0 resize-y focus:outline-none text-base leading-relaxed transition-all duration-300',
            textareaMinHeight,
            textareaMaxHeight,
            'overflow-y-auto',
            'bg-card text-foreground placeholder-gray-400 dark:placeholder-gray-500',
            readOnly && 'bg-gray-50 dark:bg-gray-800 text-muted-foreground cursor-not-allowed'
          )}
          style={{
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSize: '1rem',
            lineHeight: '1.75',
          }}
        />
      </Card>

      {/* Footer with guidelines - only for medium/long essays */}
      {!isShortAnswer && (
        <div className='p-3 bg-primary/10 border border-primary/40 rounded-lg group hover:shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/20'>
          <h4 className='text-xs font-medium text-primary/80 mb-2'>
            {t('landing.questions.essay.guidelines.title')}
          </h4>
          <ul className='text-xs text-primary dark:text-primary space-y-1'>
            <li>• {t('landing.questions.essay.guidelines.clarity')}</li>
            <li>• {t('landing.questions.essay.guidelines.vocabulary')}</li>
            <li>• {t('landing.questions.essay.guidelines.autoSave')}</li>
          </ul>
        </div>
      )}

      {/* Character/Word statistics */}
      <div className='flex justify-between text-xs text-muted-foreground'>
        <span>
          {t('landing.questions.essay.characters')}: {value.length}
        </span>
        <span className={getWordCountColor()}>
          {t('landing.questions.essay.words')}: {wordCount}
          {maxWords < Infinity ? ` / ${maxWords}` : ''}
          {isMaxWordsExceeded && ` (⚠ vượt ${wordCount - maxWords} từ)`}
        </span>
      </div>
    </div>
  );
}
